import { listChangeSlots, responseMarksTimeline, visibilityTimeline, type ResponseMarks } from '@record-fill';
import type { GeneratedRecordHistory, GeneratedRecordHistoryResponse } from '@/entities/GeneratedRecord';
import { formatInstantToPtBR } from '@/shared/lib';
import { countVisibleChanges, type CorrectedWord } from './fill-logs-text';

export const UNKNOWN_AUTHOR = 'Usuário não identificado';

export type FillLogsEntryKind = 'response' | 'erp' | 'pdf';

export interface FillLogsResponseView {
  kind: FillLogsEntryKind;
  number: number;
  authorName: string;
  instant: string;
  stageName: string;
  filledCount: number;
  correctedCount: number;
  correction: boolean;
  marks: ResponseMarks;
  fileName?: string;
}

// A correction (Ctrl+F8) corrects; any other response that changes an answer, the author's own
// edit or the sheet edit of an Upload record, alters it.
export function correctedWordOf(view: Pick<FillLogsResponseView, 'correction'> | null): CorrectedWord {
  return view?.correction ? 'corrigido' : 'alterado';
}

export function fillCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'preenchimento' : 'preenchimentos'}`;
}

type HistoryForViews = Pick<GeneratedRecordHistory, 'structure' | 'rules' | 'responses'> & {
  matrix: Pick<GeneratedRecordHistory['matrix'], 'id'>;
};

// A PDF response stores an empty diff, so it fills and corrects nothing on the sheet.
function pdfView(response: GeneratedRecordHistoryResponse): FillLogsResponseView {
  return {
    kind: 'pdf',
    number: response.number,
    authorName: response.authorName ?? UNKNOWN_AUTHOR,
    instant: formatInstantToPtBR(response.createdAt, { withTime: true }),
    stageName: response.stage.name,
    filledCount: 0,
    correctedCount: 0,
    correction: false,
    marks: { values: {}, cells: {}, newRows: {} },
    ...(response.file ? { fileName: response.file.name } : {}),
  };
}

// The ERP fields reach response 1 through the server, but the ERP entry already shows them as
// filled; marked again on response 1 they would count twice.
function withoutErpFields(marks: ResponseMarks, erpFieldIds: readonly string[]): ResponseMarks {
  if (erpFieldIds.length === 0) return marks;
  const values = { ...marks.values };
  for (const fieldId of erpFieldIds) delete values[fieldId];
  return { ...marks, values };
}

// The marks of response N need the sum of the responses before it, so every response is measured
// against the same ordered list the server sent, numbered from 1 without gaps. The chips count only
// what the text of that response lists on its own photo, never the raw marks: a change on a field a
// rule hides there would be a chip with nothing behind it.
export function buildResponseViews(
  history: HistoryForViews,
  erpFieldIds: readonly string[] = []
): FillLogsResponseView[] {
  const { responses } = history;
  const timeline = responseMarksTimeline(responses.map((response) => response.changes));
  const slots = listChangeSlots(history.structure);
  const visibility = visibilityTimeline(
    {
      matrixId: history.matrix.id,
      structure: history.structure,
      rules: history.rules,
    },
    responses
  );

  return Array.from(visibility, (isVisible, index) => {
    const response = responses[index];
    if (response.kind === 'PDF') return pdfView(response);
    const marks = index === 0 ? withoutErpFields(timeline[index], erpFieldIds) : timeline[index];
    const counts = countVisibleChanges(slots, isVisible, marks);
    return {
      kind: 'response',
      number: response.number,
      authorName: response.authorName ?? UNKNOWN_AUTHOR,
      instant: formatInstantToPtBR(response.createdAt, { withTime: true }),
      stageName: response.stage.name,
      filledCount: counts.filled,
      correctedCount: counts.corrected,
      correction: response.hasCorrections,
      marks,
    };
  });
}
